import { Card, CardHeader, Grid, Typography, Skeleton, styled, useTheme } from '@mui/material';
import React from 'react';
import { CustomWidthTooltip } from 'src/style/common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GridCellWrapper } from '../style';
import FailureScreen from 'src/frontend-common/components/FailureScreen';

interface OkOrOkMetrics {
  ko: number;
  ok: number;
}

export interface ConfusionMatrix {
  bad: OkOrOkMetrics;
  poor: OkOrOkMetrics;
  moderate: OkOrOkMetrics;
  good: OkOrOkMetrics;
  high: OkOrOkMetrics;
}

const ConfusionMatrixTable: React.FC<{
  isLoading: boolean;
  isError: boolean;
  data: ConfusionMatrix | undefined;
}> = ({ isLoading, isError, data }) => {
  const theme = useTheme();
  return (
    <StyledCard>
      <CardHeader
        title="Scoring model accuracy"
        action={
          <TooltipWrapper>
            <CustomWidthTooltip title="This visual representation, known as a confusion matrix, serves as a detailed report on the performance of the Trustfull predictive model. It systematically breaks down the outcomes of the model's predictions, mapping each score cluster to the corresponding percentage of predicted OK/KO samples.">
              <InfoOutlinedIcon />
            </CustomWidthTooltip>
          </TooltipWrapper>
        }
      />
      <ClusterWrapper isloading={isLoading ? 'true' : undefined}>
        {isLoading && (
          <Skeleton
            variant="rectangular"
            width={`calc(100% - ${theme.spacing(6)})`}
            height="100%"
            sx={{ ml: theme.spacing(3), minHeight: 345 }}
            data-testid="loading-skeleton"
          />
        )}
        {isError && <FailureScreen>Something went wrong. Please refresh the page and try again.</FailureScreen>}
        {data && (
          <Grid container justifyContent="center" data-testid="data-content">
            <AxisLabelWrapper md={1} item>
              <AxisItem md={12} item>
                <Typography variant="body2" style={{ color: 'black' }}>
                  OK
                </Typography>
              </AxisItem>
              <AxisItem md={12} item>
                <Typography variant="body2" style={{ color: 'black' }}>
                  KO
                </Typography>
              </AxisItem>
            </AxisLabelWrapper>
            {renderCells(data)}
          </Grid>
        )}
      </ClusterWrapper>
    </StyledCard>
  );
};

export default ConfusionMatrixTable;

const StyledCard = styled(Card)(() => ({
  minHeight: 362,
  height: '100%',
}));

const TooltipWrapper = styled('div')(({ theme }) => ({
  float: 'right',
  paddingRight: theme.spacing(1),
}));

const AxisLabelWrapper = styled(Grid)(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'column',
  display: 'flex',
  marginTop: theme.spacing(4.625),
}));

const AxisItem = styled(Grid)(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
}));

const ClusterWrapper = styled('div')<{ isloading?: 'true' }>(({ isloading }) => ({
  display: 'flex',
  justifyContent: isloading === 'true' ? 'flex-start' : 'center',
  flexDirection: 'column',
  minHeight: 336,
}));

const CellGrid = styled(Grid)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  flex: '1 1 0%',
  width: '100%',
  textAlign: 'center',
  backgroundColor: bgcolor,
  paddingTop: theme.spacing(4.125),
  borderBottom: '1px solid #F4F5F7',
}));

const CellBottomGrid = styled(CellGrid)({
  borderBottom: 'none',
});

const colorRanges = (value: number): string => {
  switch (true) {
    case value >= 0 && value <= 5:
      return '#D4D5FB';
    case value <= 10:
      return '#CDCEFA';
    case value <= 15:
      return '#C6C8FA';
    case value <= 20:
      return '#C0C1F9';
    case value <= 25:
      return '#B9BBF8';
    case value <= 30:
      return '#B2B4F8';
    case value <= 35:
      return '#ABAEF7';
    case value <= 40:
      return '#A4A7F7';
    case value <= 45:
      return '#9EA1F6';
    case value <= 50:
      return '#979AF5';
    case value <= 55:
      return '#9094F5';
    case value <= 60:
      return '#898DF4';
    case value <= 65:
      return '#8387F3';
    case value <= 70:
      return '#7C80F3';
    case value <= 75:
      return '#757AF2';
    case value <= 80:
      return '#6E73F2';
    case value <= 85:
      return '#676DF1';
    case value <= 90:
      return '#6166F0';
    case value <= 95:
      return '#5A60F0';
    case value <= 100:
      return '#5359EF';
    default:
      return '#D4D5FB';
  }
};

export const renderCells = (confusionMatrixData: ConfusionMatrix) => {
  return ['Poor', 'Bad', 'Moderate', 'Good', 'High'].map((cluster) => {
    const key = cluster.toLowerCase() as keyof ConfusionMatrix;
    const data = confusionMatrixData[key];

    if (!data) {
      return null;
    }

    const { ok, ko } = data;

    return (
      <Grid md={2} key={cluster} item>
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 1, color: 'black' }}>
          {cluster}
        </Typography>
        <GridCellWrapper>
          <CellGrid item md={12} bgcolor={colorRanges(ok)}>
            <Typography variant="body2" style={{ color: 'black' }}>
              {ok}%
            </Typography>
          </CellGrid>
          <CellBottomGrid item md={12} bgcolor={colorRanges(ko)}>
            <Typography variant="body2" style={{ color: 'black' }}>
              {ko}%
            </Typography>
          </CellBottomGrid>
        </GridCellWrapper>
      </Grid>
    );
  });
};
